import { useRef, useState } from "react";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { useAdoptedDraft } from "./use-adopted-draft";

/**
 * The saves of one field's typed text, and whether the last of them was refused.
 *
 * Only the latest attempt decides: an earlier save that settles after a later
 * one says nothing about the words now in the field. The words themselves stay
 * with the field; this holds only the outcome.
 */
function useSaveAttempt(save: (value: string) => Promise<void>): {
  send: (value: string) => void;
  refused: boolean;
} {
  const [refused, setRefused] = useState(false);
  const latestRef = useRef(0);

  const send = (value: string) => {
    latestRef.current += 1;
    const attempt = latestRef.current;
    void save(value).then(
      () => {
        if (attempt === latestRef.current) setRefused(false);
      },
      () => {
        if (attempt === latestRef.current) setRefused(true);
      },
    );
  };

  return { send, refused };
}

/**
 * A field's draft and its saves together: the draft follows what is stored
 * while untouched, and a refused draft counts as unsaved typing, so neither a
 * reload for a new build nor a closed tab drops it without asking.
 *
 * `stored` must be what the server has acknowledged, never a view with the
 * field's own pending save folded in: a refusal takes such a view back to the
 * old value, and the draft, matching the view it saw, would follow it and lose
 * the attorney's words.
 */
export function useSavedDraft(
  stored: string,
  save: (value: string) => Promise<void>,
) {
  const attempt = useSaveAttempt(save);
  const [draft, setDraft] = useAdoptedDraft(stored);
  const failed = attempt.refused && draft !== stored;
  useUnsavedDraft(failed);
  return { draft, setDraft, send: attempt.send, failed };
}
